import { useState } from 'react';
import { toast } from 'sonner';
import { Copy, Archive, RotateCcw } from 'lucide-react';
import type { JobRole } from '@hcc/types';
import { ApiError } from '../../lib/api';
import { useArchiveSapJob, useRestoreSapJob, useSapIgnoredJobs } from '../../hooks/useRoles';
import { Card, CardHeader, CardSection } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';

/**
 * Duplicate SAP job names, one group per contested name: the job the role
 * is linked to plus the rivals the sync could not apply. SAP cannot delete
 * a job once anyone has held it, so the fix is archiving the unwanted one
 * HERE; archiving the linked job makes the role re-link to the survivor on
 * the sync that runs straight after. Archived jobs are listed for undo.
 */
export function DuplicateSapJobsCard({ conflicted }: { conflicted: JobRole[] }) {
  const ignored = useSapIgnoredJobs();
  const archiveJob = useArchiveSapJob();
  const restoreJob = useRestoreSapJob();
  const [busyJobId, setBusyJobId] = useState<string | null>(null);
  const busy = archiveJob.isPending || restoreJob.isPending;

  const archived = ignored.data ?? [];
  if (conflicted.length === 0 && archived.length === 0) return null;

  async function archive(job: { job_id: string; name: string }) {
    setBusyJobId(job.job_id);
    try {
      await archiveJob.mutateAsync(job);
      toast.success(`SAP job ${job.job_id} archived; the list has re-synced.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not archive the SAP job');
    }
    setBusyJobId(null);
  }

  async function restore(jobId: string) {
    setBusyJobId(jobId);
    try {
      await restoreJob.mutateAsync(jobId);
      toast.success(`SAP job ${jobId} restored; the list has re-synced.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not restore the SAP job');
    }
    setBusyJobId(null);
  }

  return (
    <Card>
      <CardHeader
        icon={<Copy className="h-4 w-4" />}
        title="Duplicate SAP jobs"
        description="SAP holds more than one job with these names and cannot delete the spares (people once held them). Archive the ones not in use; archiving the linked job moves the role onto the survivor."
      />
      {conflicted.map((role) => (
        <CardSection key={role.id}>
          <div className="text-sm font-medium text-fg">{role.name}</div>
          <div className="mt-2 space-y-2">
            <JobRow
              jobId={role.external_ref!}
              detail={
                role.sap_resources.length > 0
                  ? role.sap_resources.map((r) => r.id).join(', ')
                  : 'no tariff resource'
              }
              linked
              busy={busy}
              loading={busyJobId === role.external_ref}
              onArchive={() => archive({ job_id: role.external_ref!, name: role.name })}
            />
            {role.sap_conflicts.map((c) => (
              <JobRow
                key={c.job_id}
                jobId={c.job_id}
                detail="not linked; blocked by the duplicate name"
                busy={busy}
                loading={busyJobId === c.job_id}
                onArchive={() => archive({ job_id: c.job_id, name: c.name })}
              />
            ))}
          </div>
        </CardSection>
      ))}
      {archived.length > 0 ? (
        <CardSection>
          <div className="text-xs font-medium uppercase tracking-wide text-fg-dim">
            Archived SAP jobs
          </div>
          <div className="mt-2 space-y-2">
            {archived.map((j) => (
              <div key={j.job_id} className="flex items-center justify-between gap-3">
                <div className="min-w-0 text-sm text-fg-muted">
                  <span className="font-mono">{j.job_id}</span> {j.name}
                  {j.ignored_by ? (
                    <span className="ml-2 text-xs text-fg-dim">by {j.ignored_by}</span>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<RotateCcw className="h-4 w-4" />}
                  onClick={() => restore(j.job_id)}
                  loading={restoreJob.isPending && busyJobId === j.job_id}
                  disabled={busy}
                >
                  Restore
                </Button>
              </div>
            ))}
          </div>
        </CardSection>
      ) : null}
    </Card>
  );
}

function JobRow({
  jobId,
  detail,
  linked = false,
  busy,
  loading,
  onArchive,
}: {
  jobId: string;
  detail: string;
  linked?: boolean;
  busy: boolean;
  loading: boolean;
  onArchive: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-token-sm bg-surface-2/60 px-3 py-2">
      <div className="min-w-0 text-sm">
        <span className="font-mono text-fg">{jobId}</span>
        {linked ? (
          <Pill tone="info" size="sm" className="ml-2">
            currently linked
          </Pill>
        ) : null}
        <span className="ml-2 text-xs text-fg-dim">{detail}</span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        iconLeft={<Archive className="h-4 w-4" />}
        onClick={onArchive}
        loading={loading}
        disabled={busy}
      >
        Archive
      </Button>
    </div>
  );
}
